import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { MainLayoutComponent, PageNotFoundComponent, ServerErrorComponent, UnauthorizedComponent } from '#components';
import { permissionGuard, REFRESHES_ON_POSITION_SWITCH, unauthorizedPageGuard } from '#core';
import { LocationTab } from '#enums';

/**
 * Equipment and Operational Positions are tabs of the Hierarchy workspace rather than screens of
 * their own. A plain string `redirectTo` is treated entirely as a path — it cannot carry a query
 * string — so landing an old bookmark on the right tab needs a redirect function.
 */
const toLocationTab = (tab: LocationTab) => () =>
  inject(Router).createUrlTree(['/admin/hierarchy'], { queryParams: { tab } });

export const routes: Routes = [
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [MsalGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      {
        path: 'home',
        data: { [REFRESHES_ON_POSITION_SWITCH]: true },
        loadComponent: () => import('../features/dashboard/unified-dashboard/unified-dashboard.component').then(m => m.UnifiedDashboardComponent),
      },
      {
        path: 'my-rounds',
        loadChildren: () => import('../features/my-rounds/my-rounds.routes').then(m => m.MY_ROUNDS_ROUTES),
      },
      {
        path: 'rounds',
        loadChildren: () => import('../features/rounds/rounds.routes').then(m => m.ROUNDS_ROUTES),
      },
      {
        path: 'scheduling',
        loadChildren: () => import('../features/scheduling/scheduling.routes').then(m => m.SCHEDULING_ROUTES),
      },
      {
        path: 'task-management',
        loadChildren: () => import('../features/task-management/task-management.routes').then(m => m.TASK_MANAGEMENT_ROUTES),
      },
      {
        path: 'handover',
        loadChildren: () => import('../features/handover/handover.routes').then(m => m.HANDOVER_ROUTES),
      },
      {
        path: 'notifications',
        loadChildren: () => import('../features/notifications/notifications.routes').then(m => m.NOTIFICATIONS_ROUTES),
      },
      {
        path: 'reporting',
        canActivate: [permissionGuard(['Reporting.Read'])],
        loadChildren: () => import('../features/reporting/reporting.routes').then(m => m.REPORTING_ROUTES),
      },
      {
        path: 'admin',
        loadChildren: () => import('../features/admin/admin.routes').then(m => m.ADMIN_ROUTES),
      },

      // ── Execution + Exceptions merged into Task Management (legacy redirects) ──
      { path: 'execution', redirectTo: 'my-rounds', pathMatch: 'full' },
      { path: 'execution/:id', redirectTo: 'task-management/execution/:id' },
      { path: 'exceptions', redirectTo: 'task-management/dynamic-tasks', pathMatch: 'full' },
      { path: 'exceptions/:id', redirectTo: 'task-management/exceptions/:id' },
      { path: 'ord', redirectTo: 'task-management', pathMatch: 'full' },

      // ── Hierarchy / Equipment / User Groups / Access Requests moved under Admin ──
      { path: 'hierarchy', redirectTo: 'admin/hierarchy' },
      { path: 'equipment', redirectTo: toLocationTab(LocationTab.Equipment) },
      { path: 'admin/equipment', redirectTo: toLocationTab(LocationTab.Equipment) },
      { path: 'operational-positions', redirectTo: toLocationTab(LocationTab.Positions) },
      { path: 'admin/operational-positions', redirectTo: toLocationTab(LocationTab.Positions) },
      { path: 'access-requests', redirectTo: 'admin/access-requests' },
    ],
  },
  {
    path: 'register',
    loadComponent: () => import('../features/registration/registration.component').then(m => m.RegistrationComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'access-pending',
    loadComponent: () => import('../features/registration/access-pending/access-pending.component').then(m => m.AccessPendingComponent),
    canActivate: [MsalGuard],
  },
  { path: 'unauthorized', component: UnauthorizedComponent, canActivate: [unauthorizedPageGuard] },
  { path: 'server-error', component: ServerErrorComponent },
  { path: '**', component: PageNotFoundComponent },
];
